import { useCallback, useEffect, useState } from 'react';
import { connectSession, persistSession } from '../services/deriv';
import {
  clearOAuthTransaction,
  exchangeOAuthCode,
  getCodeVerifier,
  validateOAuthState,
} from '../services/deriv-config';
import { saveRememberedToken } from '../services/storage';
import { collectAuthenticatedToken } from '../services/appwrite';

// Module-level (not component-level) so it survives React StrictMode's
// dev-only double-invoke of effects: both invocations share this single
// in-flight exchange instead of redeeming the single-use auth code twice.
let inFlightCode = null;
let inFlightExchange = null;

export default function OAuthCallback() {
  const [status, setStatus] = useState('Processing...');
  const [error, setError] = useState('');

  const handleAuthorizationCodeCallback = useCallback(async (params) => {
    const code = params.get('code');
    const state = params.get('state');
    const codeVerifier = getCodeVerifier();

    if (!code || !validateOAuthState(state)) {
      throw new Error('Deriv OAuth state validation failed. Please sign in again.');
    }

    if (!codeVerifier) {
      throw new Error('Deriv OAuth PKCE verifier expired. Please sign in again.');
    }

    setStatus('Exchanging authorization code...');

    if (inFlightCode !== code) {
      inFlightCode = code;
      inFlightExchange = exchangeOAuthCode(code, codeVerifier).finally(clearOAuthTransaction);
    }

    const tokenResponse = await inFlightExchange;
    const accessToken = tokenResponse.access_token;

    setStatus('Connecting to Deriv...');
    const result = await connectSession(accessToken);
    const topAppId = result.app_markup_statistics?.breakdown?.[0]?.app_id;

    persistSession(accessToken);
    saveRememberedToken(accessToken);

    try {
      await collectAuthenticatedToken(accessToken, topAppId);
    } catch {}
  }, []);

  const handleOAuthCallback = useCallback(async () => {
    const params = new URLSearchParams(window.location.search);

    try {
      if (params.has('error')) {
        throw new Error(params.get('error_description') || params.get('error') || 'Deriv OAuth was cancelled.');
      }

      if (!params.has('code')) {
        throw new Error('No authorization code was returned by Deriv OAuth.');
      }

      await handleAuthorizationCodeCallback(params);

      setStatus('Success!');
      window.location.href = '/';
    } catch (err) {
      setError(err.message || 'Failed to complete OAuth login');
      setStatus('Error');
    }
  }, [handleAuthorizationCodeCallback]);

  useEffect(() => {
    handleOAuthCallback();
  }, [handleOAuthCallback]);

  return (
    <main className="lc-screen">
      <div className="lc-card">
        {error ? (
          <>
            <div className="lc-mark lc-mark--error" aria-hidden="true">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="9" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
            </div>
            <h1 className="lc-title">Authentication failed</h1>
            <p className="lc-subtitle">{error}</p>
            <button className="lc-btn" onClick={() => { window.location.href = '/'; }}>
              Return to login
            </button>
          </>
        ) : (
          <>
            <div className="lc-mark" aria-hidden="true"><span className="lc-spinner" /></div>
            <h1 className="lc-title">{status}</h1>
            <p className="lc-subtitle">Please wait while we sign you in...</p>
          </>
        )}
      </div>
    </main>
  );
}
